import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref } from 'vue';
import PageLayout from "@/components_v3/layout/PageLayout.vue";
import Status from '@/components/base/Status.vue';
import BaseEditableCard from "@/components-UI/BaseEditableCard.vue";
import BaseButton from "@/components-UI/BaseButton.vue";
import BaseList from "@/components-UI/list/BaseList.vue";
import BaseListItem from "@/components-UI/list/BaseListItem.vue";
import BaseTable from "@/components-UI/table/BaseTable.vue";
import BaseGreyBacking from "@/components-UI/BaseGreyBacking.vue";
import BaseQuestionMarkTooltip from "@/components-UI/BaseQuestionMarkTooltip.vue";
import SvgPen from "@/assets/icons/pencil.svg";
import SvgTrash from "@/assets/icons/trash.svg";
import SvgCheck from "@/assets/icons/check.svg";
import SvgClose from "@/assets/icons/close.svg";
import SvgDownloadUploaded from "@/assets/icons/download-uploaded.svg";
import SvgMasterInvoice from "@/assets/icons/master-invoice.svg";
import SvgArrowRight from "@/assets/icons/arrow-right.svg";
import ViewDateTime from "@/components/base/ViewDateTime.vue";
import NumberCell from "@/components-UI/table/cells/NumberCell.vue";
import { IStatus } from "@/types";
import {
    getChargeRequestFile,
    getChargeRequestV2,
    patchChargeRequest,
} from "@/api/payout";
import {
    translateChargeRequestStatus,
    translateAffiliateStatus,
    translatePartnerStatus,
} from "@/utils/translate";
import { downloadExcelFile, ITableHeader, showNotification, showServerError } from "@/utils";
import { IAffiliateItem } from "@/api/types/user";
import { CellsType, prepareItems, RawConfig } from "@/utils/CellsFactory";
import { getEmployeesAffiliates } from "@/api/user";
import { ISingleChargeRequest } from "@/api/types/payout";
import router from "@/router";


export default /*#__PURE__*/_defineComponent({
  __name: 'ChargeRequest',
  setup(__props) {

const currentRequest = ref<ISingleChargeRequest>({
    invoices: [],
});
const pageLoading = ref(false);

const affiliateTableItems = ref([]);
const affiliatesTableHeaders = ref<ITableHeader[]>([
    { text: 'ID мастера', value: 'master_id', align: 'left', width: '120px' },
    { text: 'ID партнёра', value: 'id', align: 'left', width: '120px' },
    { text: 'Имя партнёра', value: 'affiliate_name', width: 'auto' },
    { text: 'Почта', value: 'email', width: 'auto' },
    { text: 'Аккаунт-менеджер', value: 'manager_name', width: 'auto' },
    { text: 'Договор', value: 'contract_type', width: '84px' },
    { text: 'Статус', value: 'status', width: '120px' },
]);
const affiliatesTableConfig = ref<RawConfig<IAffiliateItem>>({
    [CellsType.Route]: {
        master_id: {
            name: 'affiliate',
            valueKey: 'master_id',
            isChip: true,
            text: 'master_id',
            isMasterChip: true,
            isCopyTooltip: true,
        },
        id: {
            name: 'affiliate',
            valueKey: 'id',
            isChip: true,
            text: 'id',
            isCopyTooltip: true,
        },
    },
    [CellsType.Text]: {
        contract_type: {
            template: (i: IAffiliateItem) => !!i?.agreement_with_legal_entity ? 'Да' : 'Нет',
        },
        affiliate_name: {
            template: (i: IAffiliateItem) => `${i.first_name} ${i.last_name}`,
        },
        manager_name: {
            template: (i: IAffiliateItem) => `${i.manager?.first_name} ${i.manager?.last_name}`,
        },
    },
    [CellsType.Status]: {
        status: {
            translate: (str: string) => translatePartnerStatus(str),
        },
    },
});

const paymentsTableHeaders = ref<ITableHeader[]>([
    { text: 'ID счёта', value: 'id', align: 'center', width: '110px' },
    { text: 'Партнёра', value: 'affiliate_id', align: 'center', width: '110px' },
    { text: 'Валюта', value: 'currency' },
    { text: 'Доход', value: 'revenue', align: 'right' },
    { text: 'Выплата', value: 'payout', align: 'right' },
    { text: 'Выплачено', value: 'payments_sum', align: 'right' },
    { text: 'Остаток', value: 'balance', align: 'right' },
    { text: 'Прибыль', value: 'profit', align: 'right' },
    { text: 'Сумма выплаты', value: 'paid', align: 'right' },
    { text: 'Период оплаты', value: 'pay_period' },
    { text: 'Дата создания', value: 'created_at' },
    { text: 'Дата обновления', value: 'updated_at' },
    { text: 'Статус', value: 'status' },
]);
const paymentsTableConfig = ref<RawConfig<any>>({
    [CellsType.Route]: {
        id: {
            name: 'affiliate-invoice',
            valueKey: 'id',
            isChip: true,
            text: 'id',
            icon: (i) => i.is_master ? SvgMasterInvoice : undefined,
            tooltip: (i) => i.is_master ? 'Мастер-счёт' : '',
            iconIndent: 6,
            isTextCenter: true,
            isCopyTooltip: true,
        },
        affiliate_id: {
            name: 'affiliate',
            valueKey: 'affiliate_id',
            isChip: true,
            text: 'affiliate_id',
            isTextCenter: true,
            isCopyTooltip: true,
        },
    },
    [CellsType.Number]: {
        revenue: {},
        payout: {},
        payments_sum: {},
        balance: {},
        profit: {},
    },
    [CellsType.Date]: {
        created_at: {},
        updated_at: {},
        pay_period: {
            isHideTime: true,
        },
    },
    [CellsType.Status]: {
        status: {
            translate: (s) => translateAffiliateStatus(s),
        },
    },
    [CellsType.Uniq]: {
        paid: {},
    },
});

const colsSum = [
    'revenue',
    'payout',
    'payments_sum',
    'balance',
    'profit',
    'paid',
];

const getAffiliatesTableItems = computed(() => {
    const columns = affiliatesTableHeaders.value.map(h => h.value);
    return prepareItems(affiliateTableItems.value, affiliatesTableConfig.value, columns);
});

const getPaymentsTableItems = computed(() => {
    const columns = paymentsTableHeaders.value.map(h => h.value);
    return prepareItems(currentRequest.value.invoices, paymentsTableConfig.value, columns);
});

const isContract = computed(() => currentRequest.value.contract_type === 'contract');
const isRevolut = computed(() => currentRequest.value?.contractor?.full_name === 'Revolut');
const isEurope = computed(() => currentRequest.value?.project.name === 'Лидгид Европа');

const getListItems = computed<{ label: string, value: string, slot?: string, show?: boolean }[]>(() => {
    const commissionSum = (currentRequest.value.amount * (Number(currentRequest.value.commission) * 100) / 100)
        .toLocaleString('ru-RU', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
        }) || '0,00';

    return [
        { label: 'Наша организация', value: currentRequest.value.legal?.full_name },
        { label: 'Внешний ID', value: currentRequest.value.code },
        { label: 'Автор', value: currentRequest.value.creator },
        { label: 'Дата создания', value: currentRequest.value.created_at, slot: 'created_at' },
        { label: 'Дата обновления', value: currentRequest.value.updated_at, slot: 'updated_at' },
        { label: 'Статья  ДДС', value: currentRequest.value.dds?.name },
        { label: 'Проект', value: currentRequest.value.project?.name },
        { label: 'Способ выплат', value: isContract.value ? 'По договору' : currentRequest.value.contractor?.full_name },
        { label: 'Реквизит', value: currentRequest.value.requisites, slot: 'requisites', show: !isContract.value && !isRevolut.value },
        { label: 'Контрагент', value: isContract.value ? currentRequest.value.contractor?.full_name : '', show: isContract.value },
        { label: 'Номер инвойса', value: currentRequest.value.invoice_number, show: isContract.value && isEurope.value, slot: 'invoice' },
        { label: 'Файл', value: currentRequest.value.attachment, slot: 'file', show: isContract.value },
        { label: 'Хэш', value: currentRequest.value.hash, slot: 'hash', show: !isContract.value && !isRevolut.value },
        { label: 'Комментарий', value: currentRequest.value.comment, slot: 'comment' },
        { label: 'Сумма выплаты', value: currentRequest.value.amount, slot: 'amount' },
        { label: 'Комиссия', value: commissionSum, slot: 'commission' },
        { label: 'Итого к выплате', value: currentRequest.value.total_amount, slot: 'total_amount' },
        { label: 'Курс', value: currentRequest.value.currency_rates, slot: 'currency_rates' },
    ];
});

const getCommission = (commission: number) => computed<string>(() => {
    return (Number(commission) * 100).toLocaleString('ru-RU', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    }) || '0,00';
});

const getCurrentId = computed<string>(() => {
    return router.currentRoute.params.id;
});

const getTitle = computed<string>(() => {
    return `Заявка на расход №${getCurrentId.value}`;
});

const getStatus = computed<IStatus>(() => {
    return translateChargeRequestStatus(currentRequest.value.status);
});

const isDisabledEditButton = computed<boolean>(() => {
    return currentRequest.value.status === 'new' ||
        currentRequest.value.status === 'approved' ||
        currentRequest.value.status === 'sent';
});

const isShowFooterButtons = computed<boolean>(() => {
    return currentRequest.value.status === 'new';
});

const isDisabledDeleteChargeButton = computed(() => {
    return currentRequest.value.status === 'done' ||
        currentRequest.value.status === 'rejected';
});

const sumField = (key: string, cur: string) => computed<number>(() => {
    return currentRequest.value.invoices
        .filter(c => c.currency === cur)
        .reduce((a, b) => a + (Number(b[key]) || 0), 0);
});

const getUniqFooterPaymentsTableItems = computed(() => {
    return Array.from(new Map(currentRequest.value.invoices.map(i => [i.currency, i])).values());
});

const handleEditRequest = (): void => {
    router.push({
        name: 'edit-charge-request',
        params: {
            id: getCurrentId.value,
        },
    });
};

const downloadFile = async (): Promise<void> => {
    try {
        const report = await getChargeRequestFile(currentRequest.value.id);
        downloadExcelFile(report, 'Заявка на расход', currentRequest.value.attachment.extension);
    } catch (e) {
        showServerError(e, 'Файл не загружен');
    }
};

const changeRequestStatus = async (status: string, isDelete?: boolean): Promise<void> => {
    try {
        pageLoading.value = true;
        await patchChargeRequest([currentRequest.value.id], status);
        currentRequest.value.status = status;

        if (isDelete) {
            showNotification('Успешно! Заявка удалена');
        }
    } catch (e) {
        if (isDelete) {
            showServerError(e, 'Не удалось удалить данную заявку');
        } else {
            showServerError(e);
        }
    } finally {
        pageLoading.value = false;
    }
};

const fetchPageData = async (): Promise<void> => {
    try {
        pageLoading.value = true;
        currentRequest.value = await getChargeRequestV2(getCurrentId.value);

        const affiliates = await getEmployeesAffiliates({
            ids: currentRequest.value.affiliates,
            fields: [
                'master_id',
                'id',
                'first_name',
                'last_name',
                'email',
                'manager',
                'agreement_with_legal_entity',
                'status',
            ],
        });
        affiliateTableItems.value = affiliates.data;
    } catch (e) {
        showServerError(e, 'Данные не загружены');
    } finally {
        pageLoading.value = false;
    }
};

onMounted(async (): Promise<void> => {
    await fetchPageData();
});

return { __sfc: true,currentRequest, pageLoading, affiliateTableItems, affiliatesTableHeaders, affiliatesTableConfig, paymentsTableHeaders, paymentsTableConfig, colsSum, getAffiliatesTableItems, getPaymentsTableItems, isContract, isRevolut, isEurope, getListItems, getCommission, getCurrentId, getTitle, getStatus, isDisabledEditButton, isShowFooterButtons, isDisabledDeleteChargeButton, sumField, getUniqFooterPaymentsTableItems, handleEditRequest, downloadFile, changeRequestStatus, fetchPageData, PageLayout, Status, BaseEditableCard, BaseButton, BaseList, BaseListItem, BaseTable, BaseGreyBacking, BaseQuestionMarkTooltip, SvgPen, SvgTrash, SvgCheck, SvgClose, SvgDownloadUploaded, SvgArrowRight, ViewDateTime, NumberCell, router }
}

})